import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  ListItemButton,
  ListItemText,
  Divider,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import * as Constant from "../../features/constant";
import { useGetNotificationListQuery } from "../../features/api/notificationApiSlice";
import useNotificationCount from "../../hooks/useNotificationCount";
import MessageTable from "./MessageTable";
import MessageListMobile from "./MessageListMobile";
import MessageDetail from "./MessageDetail";

const CATEGORIES = [
  { code: "PROMOTION", labelKey: "messaging.promotions", fallback: "Promotions" },
  { code: "PAYMENT", labelKey: "messaging.payment", fallback: "Payment" },
  { code: "SYSTEM", labelKey: "messaging.system", fallback: "System" },
  { code: "NOTIFICATION", labelKey: "messaging.notification", fallback: "Notification" },
  { code: "REWARDS_CLUB", labelKey: "messaging.rewardsclub", fallback: "Rewards Club" },
];

const MessagingPage = ({ initialCategory = "PROMOTION", embedded = false, onCategoryChange }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down("lg"));
  const isLightTheme = process.env.REACT_APP_IS_LIGHT_THEME === "true";

  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [page, setPage] = useState(0);

  const { counts } = useNotificationCount();

  const {
    data: notificationListData,
    isLoading: isListLoading,
    isFetching: isListFetching,
  } = useGetNotificationListQuery({
    category: selectedCategory,
    page,
    size: 10,
  });

  const messages = notificationListData?.result?.content || [];
  const totalPages = notificationListData?.result?.totalPages || 0;
  const totalElements = notificationListData?.result?.totalElements || 0;

  // Reset page and selections when category changes
  useEffect(() => {
    setPage(0);
    setSelectedIds(new Set());
    setSelectedMessageId(null);
  }, [selectedCategory]);

  // Sync with parent when initialCategory prop changes (embedded mode)
  useEffect(() => {
    if (initialCategory !== selectedCategory) {
      setSelectedCategory(initialCategory);
    }
  }, [initialCategory]);

  const handleCategoryChange = (categoryCode) => {
    setSelectedCategory(categoryCode);
    if (onCategoryChange) onCategoryChange(categoryCode);
  };

  const handleTabChange = (event, newValue) => {
    const newCategory = CATEGORIES[newValue].code;
    setSelectedCategory(newCategory);
    if (onCategoryChange) onCategoryChange(newCategory);
  };

  const handleMessageClick = (messageId) => {
    setSelectedMessageId(messageId);
  };

  const handleBackToList = () => {
    setSelectedMessageId(null);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage - 1);
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === messages.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(messages.map((m) => m.id)));
    }
  };

  const selectedTabIndex = CATEGORIES.findIndex((c) => c.code === selectedCategory);

  // Mobile layout
  if (mobileView) {
    return (
      <Box
        sx={{
          backgroundColor: embedded ? "transparent" : theme.palette.background.secondaryPaper,
          minHeight: embedded ? "auto" : "100vh",
          pt: embedded ? 1 : 7,
          pb: embedded ? 1 : 8,
        }}
      >
        {selectedMessageId ? (
          <>
            {/* In-page back-to-list row — page-level title/back nav comes from the shared MobileHeader */}
            <Box
              onClick={handleBackToList}
              sx={{
                display: "flex",
                alignItems: "center",
                px: 2,
                py: 1.5,
                cursor: "pointer",
              }}
            >
              <ArrowBack sx={{ color: process.env.REACT_APP_COLOR_TEXT || "white", fontSize: 20, mr: 1 }} />
              <Typography
                variant="body2"
                sx={{ color: process.env.REACT_APP_COLOR_TEXT || "white", fontWeight: 600 }}
              >
                {t("messaging.backtolist", "Back")}
              </Typography>
            </Box>
            <MessageDetail messageId={selectedMessageId} />
          </>
        ) : (
          <>
            {/* Category tabs */}
            <Tabs
              value={selectedTabIndex}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                backgroundColor: theme.palette.background.paper,
                "& .MuiTab-root": {
                  color: process.env.REACT_APP_COLOR_TEXT || "white",
                  textTransform: "none",
                  fontSize: "13px",
                  minWidth: "auto",
                  px: 2,
                },
                "& .Mui-selected": {
                  color: `${theme.palette.primary.main} !important`,
                  fontWeight: 600,
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            >
              {CATEGORIES.map((cat) => (
                <Tab
                  key={cat.code}
                  label={
                    <Box sx={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                      {t(cat.labelKey, cat.fallback)}
                      {(counts?.[cat.code] || 0) > 0 && (
                        <Box
                          component="span"
                          sx={{
                            ml: 0.8,
                            backgroundColor: "#d32f2f",
                            color: "white",
                            fontSize: "10px",
                            fontWeight: 600,
                            lineHeight: 1,
                            height: 18,
                            minWidth: 18,
                            borderRadius: "9px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "0 4px",
                          }}
                        >
                          {counts[cat.code]}
                        </Box>
                      )}
                    </Box>
                  }
                />
              ))}
            </Tabs>

            {/* Message list */}
            <MessageListMobile
              messages={messages}
              isLoading={isListLoading || isListFetching}
              onMessageClick={handleMessageClick}
              page={page + 1}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              totalElements={totalElements}
            />
          </>
        )}
      </Box>
    );
  }

  // Desktop layout
  const desktopContent = (
        <Grid container direction="row" spacing={3}>
          {/* Desktop sidebar — hidden when embedded in ProfileWallet */}
          {!embedded && <Grid item xs={3}>
            <Box
              sx={{
                bgcolor:
                  process.env.REACT_APP_COLOR_BACKGROUND_TERTIARY ||
                  theme.palette.background.paper,
                borderRadius: 3,
                boxShadow: "0px 4px 10px rgba(0,0,0,0.05)",
                py: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  px: 2,
                  pb: 1,
                  color: process.env.REACT_APP_COLOR_TEXT || "white",
                  fontWeight: 600,
                  fontSize: "16px",
                }}
              >
                {t("messaging.title", "Messaging")}
              </Typography>
              <Divider sx={{ borderColor: theme.palette.divider1 }} />

              {CATEGORIES.map((cat) => {
                const isActive = selectedCategory === cat.code;
                const count = counts?.[cat.code] || 0;

                return (
                  <ListItemButton
                    key={cat.code}
                    selected={isActive}
                    onClick={() => handleCategoryChange(cat.code)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderLeft: isActive
                        ? `3px solid ${theme.palette.primary.main}`
                        : "3px solid transparent",
                      backgroundColor: isActive
                        ? `${theme.palette.primary.main}15`
                        : "transparent",
                      "&:hover": {
                        backgroundColor: `${theme.palette.primary.main}10`,
                      },
                    }}
                  >
                    <ListItemText
                      primary={t(cat.labelKey, cat.fallback)}
                      primaryTypographyProps={{
                        fontSize: "14px",
                        fontWeight: isActive ? 600 : 400,
                        color: isActive
                          ? theme.palette.primary.main
                          : process.env.REACT_APP_COLOR_TEXT || "white",
                      }}
                    />
                    {count > 0 && (
                      <Box
                        component="span"
                        sx={{
                          backgroundColor: "#d32f2f",
                          color: "white",
                          fontSize: "11px",
                          fontWeight: 600,
                          lineHeight: 1,
                          height: 20,
                          minWidth: 20,
                          borderRadius: "10px",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "0 5px",
                        }}
                      >
                        {count}
                      </Box>
                    )}
                  </ListItemButton>
                );
              })}
            </Box>
          </Grid>}

          {/* Desktop content */}
          <Grid item xs={embedded ? 12 : 9}>
            <Box
              sx={{
                bgcolor:
                  process.env.REACT_APP_COLOR_BACKGROUND_TERTIARY ||
                  theme.palette.background.paper,
                borderRadius: 3,
                boxShadow: "0px 4px 10px rgba(0,0,0,0.05)",
                p: 2,
                minHeight: "60vh",
              }}
            >
              {/* Category tabs at top of content area */}
              <Tabs
                value={selectedTabIndex}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  mb: 2,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: 500,
                    minWidth: 120,
                    color: "white",
                    backgroundColor: theme.palette.secondary.main,
                    mx: 0.3,
                    borderRadius: "4px 4px 0 0",
                    opacity: 0.7,
                  },
                  "& .Mui-selected": {
                    opacity: 1,
                    color: "white !important",
                    backgroundColor: theme.palette.primary.main,
                  },
                  "& .MuiTabs-indicator": {
                    display: "none",
                  },
                }}
              >
                {CATEGORIES.map((cat) => (
                  <Tab
                    key={cat.code}
                    label={`${t(cat.labelKey, cat.fallback)} (${counts?.[cat.code] || 0})`}
                  />
                ))}
              </Tabs>

              {selectedMessageId ? (
                <MessageDetail
                  messageId={selectedMessageId}
                  onBack={handleBackToList}
                />
              ) : (
                <MessageTable
                  messages={messages}
                  isLoading={isListLoading || isListFetching}
                  selectedIds={selectedIds}
                  onToggleSelect={handleToggleSelect}
                  onSelectAll={handleSelectAll}
                  onMessageClick={handleMessageClick}
                  page={page + 1}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  totalElements={totalElements}
                  category={selectedCategory}
                />
              )}
            </Box>
          </Grid>
        </Grid>
  );

  // When embedded in ProfileWallet, skip the outer wrapper/container
  if (embedded) {
    return desktopContent;
  }

  return (
    <Box
      px={2}
      py={3}
      sx={{
        backgroundColor: theme.palette.background.secondaryPaper,
        minHeight: "80vh",
      }}
    >
      <Container>
        {desktopContent}
      </Container>
    </Box>
  );
};

export default MessagingPage;
